import isObject from './isObject.js';

const escapeMap = {
	'&': '&amp;',
	'<': '&lt;',
	'>': '&gt;',
	'"': '&quot;',
	'\'': '&#39;',
	'`': '&#96;'
};
const desanitizeMap = {
	...Object.fromEntries(Object.entries(escapeMap).map((a) => a.reverse())),
	'&nbsp;': ' '
};
const escapeKeys = Object.keys(escapeMap).join('');
const desanitizeKeys = Object.keys(desanitizeMap).join('|');
const escapeRegExp = new RegExp(`[${escapeKeys}]`, 'g');
const desanitizeRegExp = new RegExp(`(${desanitizeKeys})`, 'g');
const replaceCharCodeRegExp = /&#(\d+|x\w+);/g;
const needsEscapeRegExp = new RegExp(escapeRegExp.source);
const needsDesanitizeRegExp = new RegExp(desanitizeRegExp.source);
const needsReplaceCharCodeRegExp = new RegExp(replaceCharCodeRegExp.source);

/**
 * Does value need to be escaped?
 * @param {*} value
 * @returns {boolean}
 */
export const needsEscape = (value) => {
	const stringValue = String(value);
	return needsEscapeRegExp.test(stringValue);
};

/**
 * Does value need to be desanitized?
 * @param {*} value
 * @returns {boolean}
 */
export const needsDesanitize = (value) => {
	const stringValue = String(value);
	return needsDesanitizeRegExp.test(stringValue);
};

/**
 * Does value contain char codes?
 * @param {*} value
 * @returns {boolean}
 */
export const needsReplaceCharCode = (value) => {
	const stringValue = String(value);
	return needsReplaceCharCodeRegExp.test(stringValue);
};

/**
 * Escape single character
 * @param {*} value
 * @returns {string}
 */
export const escapeChar = (value) => {
	return escapeMap[value] || String(value);
};

/**
 * Desanitize single character
 * @param {*} value
 * @returns {string}
 */
export const desanitizeChar = (value) => {
	return desanitizeMap[value] || String(value);
};

/**
 * Replace single char code
 * @param {*} match
 * @param {string} string
 * @returns {string}
 */
export const replaceCharCode = (match, string) => {
	// Check if the match is a special symbol (e.g. &#x0E3F; | ฿)
	const isHexCodeReg = /&#(x\w+);/g.test(match);
	// Some currency symbols need to lead with a zero to convert properly (hex codes) - more info in CART-2700
	const number = isHexCodeReg ? `0${string}` : parseInt(string, 10);
	const replaced = String.fromCharCode(number);
	return replaced === '\u0000' ? match : replaced; // Return original match in case of \u0000 (null)
};

/**
 * Escape string of markup
 * @param {*} value
 * @returns {string}
 */
export const escapeMarkup = (value) => {
	if (needsEscape(value)) {
		const stringValue = String(value);
		return stringValue.replace(escapeRegExp, escapeChar);
	} else {
		return value;
	}
};

/**
 * Desanitize string of markup
 * @param {*} value
 * @returns {string}
 */
export const desanitizeMarkup = (value) => {
	if (needsDesanitize(value)) {
		const stringValue = String(value);
		return stringValue.replace(desanitizeRegExp, desanitizeChar);
	} else {
		return value;
	}
};

/**
 * Replace char codes in string of markup
 * @param {*} value
 * @returns {string}
 */
export const replaceCharCodesInMarkup = (value) => {
	if (needsReplaceCharCode(value)) {
		const stringValue = String(value);
		return stringValue.replace(replaceCharCodeRegExp, replaceCharCode);
	} else {
		return value;
	}
};

/**
 * Escape every string in an object
 * @param {object} object
 * @returns {object}
 */
export const escapeObject = (object) => {
	if (isObject(object)) {
		return Object.entries(object).reduce((previous, [key, value]) => {
			previous[escapeMarkup(key)] = isObject(value) ? escapeObject(value) : escapeMarkup(value);
			return previous;
		}, {});
	} else {
		return object;
	}
};

/**
 * Desanitize every string in an object
 * @param {object} object
 * @returns {object}
 */
export const desanitizeObject = (object) => {
	if (isObject(object)) {
		return Object.entries(object).reduce((previous, [key, value]) => {
			previous[desanitizeMarkup(key)] = isObject(value) ? desanitizeObject(value) : desanitizeMarkup(value);
			return previous;
		}, {});
	} else {
		return object;
	}
};

/**
 * Replace char codes in every string in an object
 * @param {object} object
 * @returns {object}
 */
export const replaceCharCodesInObject = (object) => {
	if (isObject(object)) {
		return Object.entries(object).reduce((previous, [key, value]) => {
			previous[replaceCharCodesInMarkup(key)] = isObject(value) ? replaceCharCodesInObject(value) : replaceCharCodesInMarkup(value);
			return previous;
		}, {});
	} else {
		return object;
	}
};
